import { useState, useRef, memo, useEffect } from 'react';

import cn from 'classnames';
import { motion, useAnimation } from 'framer-motion';
import { IoIosArrowForward, IoIosArrowBack } from 'react-icons/io';

import VideoBox from './VideoBox';

import { useDrawerStore } from '@src/stores';

import { IVideoStream } from '@src/types';

import './PinnedVideo.scss';

type PinnedVideoProps = {
  videoStream: IVideoStream;
  isPinned?: boolean;
};

const PinnedVideo = ({ videoStream, isPinned }: PinnedVideoProps) => {
  const { toggleDrawer, isDrawerOpen, selectedKey } = useDrawerStore();
  const controls = useAnimation();
  const videoRef = useRef<HTMLDivElement>(null);

  const [isHidden, setIsHidden] = useState(false);
  const [arrowPosition, setArrowPosition] = useState({ x: 0, y: -20 });

  const [pinnedSide, setPinnedSide] = useState<'left' | 'right'>('right');
  const disappearDuration = 0.2;
  const arrowDelay = 100;

  // Track window dimensions dynamically
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(() => window.innerHeight);
  // Track current position to avoid using getValues() which doesn't exist
  const currentPositionRef = useRef({ x: window.innerWidth - (isPinned ? 203 : 124), y: 0 });

  const pinnedWidth = isPinned ? 203 : 124;
  const defaultPinnedX = windowWidth - pinnedWidth;

  const calculatePositionY = (pointY: number) => {
    if (pointY < 100) {
      return 100;
    } else {
      if (selectedKey === 'chat') {
        return pointY > windowHeight - 300 ? windowHeight - 300 : pointY;
      }
      return pointY > windowHeight - 150 ? windowHeight - 150 : pointY;
    }
  };

  const showVideo = () => {
    setIsHidden(false);
    let positionY = arrowPosition.y - (isPinned ? 160 : 160);
    const newX = pinnedSide === 'left' ? 10 : defaultPinnedX;
    const newY = positionY < 0 ? 0 : positionY;

    currentPositionRef.current = { x: newX, y: newY };
    controls.start({
      x: newX,
      y: newY,
      transition: { duration: 0.3, ease: 'easeOut' },
    });
  };

  const onDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: any) => {
    if (!isDrawerOpen) {
      const midpoint = windowWidth / 2;
      const snapTo = info.point.x < midpoint ? 10 : defaultPinnedX;
      currentPositionRef.current = { x: snapTo, y: currentPositionRef.current.y };
      controls.start({
        x: snapTo,
        transition: { duration: 0.3, ease: 'easeOut' },
      });
      return;
    }

    if (info.point.x > windowWidth - 60 || info.point.x < 60) {
      const side = info.point.x < windowWidth / 2 ? 'left' : 'right';
      setPinnedSide(side);

      setArrowPosition({ x: info.point.x, y: calculatePositionY(info.point.y) });
      const targetX = side === 'left' ? -pinnedWidth : windowWidth;
      currentPositionRef.current = { x: targetX, y: currentPositionRef.current.y };
      controls
        .start({
          x: targetX,
          transition: { duration: disappearDuration, ease: 'easeOut' },
        })
        .then(() => {
          setTimeout(() => setIsHidden(true), arrowDelay);
        });
      return;
    }
    const midpoint = windowWidth / 2;
    const snapTo = info.point.x < midpoint ? 10 : defaultPinnedX;
    currentPositionRef.current = { x: snapTo, y: currentPositionRef.current.y };
    controls.start({
      x: snapTo,
      transition: { duration: 0.3, ease: 'easeOut' },
    });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        setWindowWidth(newWidth);
        setWindowHeight(newHeight);

        const newDefaultPinnedX = newWidth - pinnedWidth;
        const minX = 10;

        // Always snap to the correct edge based on pinnedSide
        const validX = pinnedSide === 'left' ? minX : newDefaultPinnedX;

        const currentY = currentPositionRef.current.y;
        const maxY = newHeight - 250;
        let validY = currentY;

        if (currentY == null || currentY < 0) {
          validY = 0;
        } else if (currentY > maxY) {
          validY = maxY;
        }

        currentPositionRef.current = { x: validX, y: validY };

        controls.start({
          x: validX,
          y: validY,
          transition: { duration: 0.3, ease: 'easeOut' },
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [controls, pinnedWidth, pinnedSide]);

  return (
    <>
      {isHidden && (
        <div
          className="pinned-video-arrow"
          style={{
            position: 'absolute',
            top: arrowPosition.y - 60,
            [pinnedSide === 'left' ? 'left' : 'right']: '-40px',
            display: 'flex',
            justifyContent: pinnedSide === 'left' ? 'flex-end' : 'flex-start',
            cursor: 'pointer',
          }}
          onClick={showVideo}
        >
          {pinnedSide === 'left' ? <IoIosArrowForward /> : <IoIosArrowBack />}
        </div>
      )}

      <motion.div
        ref={videoRef}
        className={cn('pinned-video', { pinned: isPinned })}
        drag
        dragConstraints={{
          left: 10,
          right: windowWidth - pinnedWidth,
          top: 0,
          bottom: windowHeight - 250,
        }}
        dragElastic={0.2}
        animate={controls}
        initial={{ x: defaultPinnedX, y: 0 }}
        whileDrag={{ scale: 1.05 }}
        onDragEnd={onDragEnd}
        onDrag={(event, info) => {
          // Update position ref during drag
          currentPositionRef.current = { x: info.point.x, y: info.point.y };
        }}
      >
        <VideoBox
          {...videoStream}
          showName={false}
          displaySmallAvatar={isPinned}
          onVideoClick={() => {
            toggleDrawer(false);
          }}
        />
      </motion.div>
    </>
  );
};

export default memo(PinnedVideo);
